import { Component, ViewChild, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MenuGroupService, MenuGroup } from '../../services/menu-group.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModuleService } from '../../services/module.service';
import { catchError, finalize, filter } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { DeleteMenuGroupDialogComponent } from './delete-menu-group-dialog/delete-menu-group-dialog.component';

@Component({
    selector: 'app-menu-groups-page',
    standalone: true,
    imports: [
        CommonModule,
        NgIf,
        RouterOutlet,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatPaginatorModule,
        MatTableModule,
        MatSortModule,
        MatSnackBarModule,
        MatDialogModule,
        MatTooltipModule,
    ],
    templateUrl: './menu-groups-page.component.html',
    styleUrl: './menu-groups-page.component.scss',
})
export class MenuGroupsPageComponent implements OnInit, AfterViewInit, OnDestroy {
    displayedColumns: string[] = ['nombre', 'orden', 'action'];
    dataSource = new MatTableDataSource<MenuGroupRow>([]);
    isLoading = false;
    private routerSubscription?: Subscription;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private menuGroupService: MenuGroupService,
        // eslint-disable-next-line no-unused-vars
        private moduleService: ModuleService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private dialog: MatDialog,
        // eslint-disable-next-line no-unused-vars
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadMenuGroups();
        this.subscribeToRouterEvents();
    }

    ngOnDestroy(): void {
        this.routerSubscription?.unsubscribe();
    }

    private subscribeToRouterEvents(): void {
        this.routerSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const url = event.urlAfterRedirects;
                if (url === '/modules/menu-groups' || url.startsWith('/modules/menu-groups?')) {
                    this.loadMenuGroups();
                }
            });
    }

    ngAfterViewInit(): void {
        if (this.paginator) this.dataSource.paginator = this.paginator;
        if (this.sort) this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (item: MenuGroupRow, prop: string) => {
            if (prop === 'nombre') return item.nombre;
            if (prop === 'orden') return item.orden.toString();
            return (item as unknown as Record<string, string>)[prop] ?? '';
        };
    }

    loadMenuGroups(): void {
        this.isLoading = true;
        this.menuGroupService
            .getMenuGroups()
            .pipe(
                catchError((err) => {
                    this.logger.error('Error al cargar grupos de menú', err as Error, 'MenuGroupsPageComponent');
                    this.snackBar.open('Error al cargar grupos de menú', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of({ success: false, data: [] });
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((res) => {
                if (res.success && res.data) {
                    this.dataSource.data = res.data.map((g) => ({ id: g.id, nombre: g.nombre, orden: g.orden }));
                    this.cdr.detectChanges();
                }
            });
    }

    editGroup(row: MenuGroupRow): void {
        this.router.navigate(['/modules/menu-groups/edit', row.id]);
    }

    deleteGroup(row: MenuGroupRow): void {
        this.menuGroupService.getMenuGroupById(row.id).subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    this.openDeleteDialog(res.data);
                }
            },
            error: () => {
                this.snackBar.open('Error al obtener el grupo', 'Cerrar', {
                    duration: 3000,
                    panelClass: ['error-snackbar'],
                });
            },
        });
    }

    private openDeleteDialog(group: MenuGroup): void {
        const d = this.dialog.open(DeleteMenuGroupDialogComponent, {
            width: '500px',
            maxWidth: '90vw',
            disableClose: true,
            data: { menuGroup: group },
        });
        d.afterClosed().subscribe((ok) => {
            if (ok) this.performDelete(group.id);
        });
    }

    private performDelete(id: string): void {
        this.isLoading = true;
        this.menuGroupService
            .deleteMenuGroup(id)
            .pipe(
                catchError((err) => {
                    const msg = err.error?.message || 'Error al eliminar el grupo de menú';
                    this.snackBar.open(msg, 'Cerrar', { duration: 6000, panelClass: ['error-snackbar'] });
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((res) => {
                if (res?.success) {
                    this.moduleService.notifyModulesChanged();
                    this.loadMenuGroups();
                    this.snackBar.open('Grupo de menú eliminado exitosamente', 'Cerrar', {
                        duration: 3000,
                        panelClass: ['success-snackbar'],
                    });
                }
            });
    }

    onCreate(): void {
        this.router.navigate(['/modules/menu-groups/create']);
    }

    onBack(): void {
        this.router.navigate(['/modules']);
    }

    isBaseRoute(): boolean {
        const u = this.router.url || '';
        return u === '/modules/menu-groups' || u === '/modules/menu-groups/' || u.startsWith('/modules/menu-groups?');
    }
}

export interface MenuGroupRow {
    id: string;
    nombre: string;
    orden: number;
}
